/**
 * The events dashboard, at the top of the main page (2026-10-23, the owner: "make the event a
 * very important matter and have it on the main page all the time, like a dashboard thing").
 *
 * Always there, even with no events: what is coming up, how many have answered and how many
 * are coming, then each event with its date, how long until it, and its answers. An event
 * opens its card in the editor with the guest list showing (?invite=1). The answers come from
 * the server (apps/api/src/events.ts) and are read again whenever the page comes back into view.
 * Past events fold away under their own heading.
 *
 * Later the same day: an event lists its cards (one for family, one for friends...) and can have
 * another added; it can be deleted, after an "are you sure?", into the archive at the foot of
 * the board, where it can be restored for 30 days or deleted for good.
 *
 * 2026-10-24: an event's name, date, address and guests' names come from this device
 * (local/events.ts), the answers from the server. An event whose details are on another device
 * shows its answers and its date (the server keeps the day it deletes it, 90 days after). Events
 * from before move their details here the first time the board sees them (takeAllLegacy).
 */
import { useCallback, useEffect, useState } from 'react';
import { api, type EventBrief, type EventSummary, type ProjectItem } from './api.ts';
import { askConfirm } from './confirm.tsx';
import { archiveEvent, eventTitle, kindLabel, localEventsById, takeAllLegacy, whenOf } from './invite.tsx';
import { deleteLocalEvent, onLocalEvents, type LocalEvent } from './local/events.ts';
import { Link } from './router.tsx';
import { ago, t } from './strings.ts';

type Archived = EventBrief & { purgeOn: string };

/** Whole days from today to the event's date, both as the host's own calendar has them. */
export function daysUntil(startsOn: string, today = new Date()): number {
  const [y, m, d] = startsOn.split('-').map(Number);
  const day = Date.UTC(y, m - 1, d);
  const now = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((day - now) / 86_400_000);
}

/** The event's date: from this device, or - for one whose details are elsewhere - 90 days before the server deletes it. */
function dateOf(x: EventBrief, details: LocalEvent | undefined): string {
  if (details) return details.startsOn;
  if (!x.event.keepUntil) return new Date().toISOString().slice(0, 10);
  const [y, m, d] = x.event.keepUntil.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d - 90)).toISOString().slice(0, 10);
}

function countdown(n: number): string {
  if (n === 0) return t('eventsToday');
  if (n === 1) return t('eventsTomorrow');
  if (n === -1) return t('eventsYesterday');
  return n > 0 ? t('eventsInDays', { n }) : t('eventsDaysAgo', { n: -n });
}

/** The date as a little calendar leaf: the month over the day. */
function DateLeaf({ startsOn }: { startsOn: string }) {
  const [y, m, d] = startsOn.split('-').map(Number);
  const month = new Date(y, m - 1, d).toLocaleDateString(undefined, { month: 'short' });
  return (
    <span className="ev-leaf" aria-hidden="true">
      <span className="ev-month">{month}</span>
      <span className="ev-day">{d}</span>
    </span>
  );
}

function Answers({ s }: { s: EventSummary }) {
  if (!s.guests) return <span className="muted small">{t('eventsNoGuests')}</span>;
  return (
    <span className="ev-answers">
      <span className="rsvp-chip yes">{t('rsvpYes')} {s.yes}</span>
      <span className="rsvp-chip maybe">{t('rsvpMaybe')} {s.maybe}</span>
      <span className="rsvp-chip no">{t('rsvpNo')} {s.no}</span>
      <span className="rsvp-chip waiting">{t('rsvpWaiting')} {s.waiting}</span>
    </span>
  );
}

const guestsLabel = (n: number) => (n === 1 ? t('eventsCardGuestsOne') : t('eventsCardGuests', { n }));

function EventRow({ item, details, projects, onAddCard, onDelete }: {
  item: EventBrief; details: LocalEvent | undefined; projects: ProjectItem[] | null; onAddCard?: () => void; onDelete: () => void;
}) {
  const { summary, cards } = item;
  const startsOn = dateOf(item, details);
  const n = daysUntil(startsOn);
  // an event's cards, those on this device by name; one with no card of its own yet has none
  const here = cards.map((c) => ({ ...c, card: projects?.find((p) => p.id === c.projectId) }));
  return (
    <li className={`ev-row${n < 0 ? ' past' : n <= 7 ? ' soon' : ''}`}>
      <DateLeaf startsOn={startsOn} />
      <span className="ev-main">
        <strong className="ev-title">{eventTitle(details)}</strong>
        {details
          ? <span className="muted small">{kindLabel(details.kind)} · {whenOf(details)}</span>
          : <span className="muted small">{t('eventsElsewhereHint')}</span>}
        {details?.place && <span className="muted small ev-place">{details.place}</span>}
      </span>
      <span className="ev-side">
        <span className="ev-countdown">{countdown(n)}</span>
        <Answers s={summary} />
        {summary.coming > 0 && <span className="muted small">{t('rsvpComing', { n: summary.coming })}</span>}
      </span>
      <span className="ev-cards">
        {here.length === 0 && <span className="muted small">{t('eventsCardElsewhere')}</span>}
        {here.map((c) => (c.card
          ? <Link key={c.projectId} href={`/projects/${c.projectId}?invite=1`} className="button small ev-card" title={t('eventsOpen')}>
              {c.card.title}<span className="muted"> · {guestsLabel(c.guests)}</span>
            </Link>
          : <span key={c.projectId} className="muted small ev-card-away">{t('eventsCardElsewhere')} · {guestsLabel(c.guests)}</span>))}
        {onAddCard && <button className="small ev-add" onClick={onAddCard} title={t('eventsAddCardHint')}>+ {t('eventsAddCard')}</button>}
        <button className="small icon-btn ev-delete" onClick={onDelete} title={t('eventsDelete')} aria-label={`${t('eventsDelete')}: ${eventTitle(details)}`}>
          <svg className="btn-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M5 7h14M10 7V5h4v2M7 7l1 12h8l1-12M10 11v5M14 11v5" /></svg>
        </button>
      </span>
    </li>
  );
}

export function EventsBoard({ projects, onAddCard }: {
  projects: ProjectItem[] | null;
  /** Make another card for this event (new-card.tsx). */
  onAddCard: (event: LocalEvent) => void;
}) {
  const [items, setItems] = useState<EventBrief[] | null>(null);
  const [archive, setArchive] = useState<Archived[]>([]);
  const [local, setLocal] = useState<Map<string, LocalEvent>>(new Map());
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const load = useCallback(async () => {
    try {
      let list = (await api.events()).events;
      // events from before 2026-10-24: their details come to this device, and leave the server
      if (await takeAllLegacy(list)) list = (await api.events()).events;
      setItems(list);
      setLocal(await localEventsById());
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    }
    api.archivedEvents().then((r) => setArchive(r.events), () => {});
  }, []);
  useEffect(() => {
    void load();
    // answers arrive while the host is elsewhere: read them again on coming back to the page
    const back = () => { if (document.visibilityState === 'visible') void load(); };
    document.addEventListener('visibilitychange', back);
    const off = onLocalEvents(() => { void localEventsById().then(setLocal); });
    return () => { document.removeEventListener('visibilitychange', back); off(); };
  }, [load]);
  const act = async (job: () => Promise<string | null>) => {
    setError(null);
    try {
      const said = await job();
      if (said) setNote(said);
      await load();
    } catch (err) {
      setError((err as Error).message);
    }
  };
  const titleOf = (id: string) => eventTitle(local.get(id));
  const remove = (id: string) => act(async () => ((await archiveEvent({ id, title: titleOf(id) })) ? t('eventsDeleted', { title: titleOf(id) }) : null));
  const restore = (id: string) => act(async () => { await api.restoreEvent(id); return t('eventsRestored', { title: titleOf(id) }); });
  const forever = (id: string) => act(async () => {
    const yes = await askConfirm({ title: t('eventsForeverTitle', { title: titleOf(id) }), body: t('eventsForeverBody'), yes: t('eventsForever'), danger: true });
    if (!yes) return null;
    await api.deleteEventForever(id);
    await deleteLocalEvent(id);
    return null;
  });

  const dated = (items ?? []).map((x) => ({ x, on: dateOf(x, local.get(x.event.id)), at: local.get(x.event.id)?.startsAt ?? '' }));
  const upcoming = dated.filter((d) => daysUntil(d.on) >= 0).sort((a, b) => a.on.localeCompare(b.on) || a.at.localeCompare(b.at)).map((d) => d.x);
  const past = dated.filter((d) => daysUntil(d.on) < 0).sort((a, b) => b.on.localeCompare(a.on)).map((d) => d.x);
  const total = (k: keyof EventSummary) => upcoming.reduce((sum, x) => sum + x.summary[k], 0);
  const row = (x: EventBrief) => {
    const details = local.get(x.event.id);
    return (
      <EventRow key={x.event.id} item={x} details={details} projects={projects}
        onAddCard={details ? () => onAddCard(details) : undefined} onDelete={() => void remove(x.event.id)} />
    );
  };

  return (
    <section className="events-board" aria-labelledby="events-title">
      <div className="events-head">
        <h2 id="events-title">{t('eventsTitle')}</h2>
      </div>
      {error && <p className="error small" role="alert">{error}</p>}
      {note && <p className="ok small" role="status" onClick={() => setNote(null)}>{note}</p>}
      {items === null ? (!error && <p className="muted small">{t('loading')}</p>) : items.length === 0 ? (
        <p className="events-empty">{t('eventsEmpty')}</p>
      ) : (
        <>
          <div className="ev-stats">
            <span className="ev-stat"><strong>{upcoming.length}</strong><span className="muted small">{t('eventsUpcoming')}</span></span>
            <span className="ev-stat"><strong>{t('eventsAnsweredOf', { n: total('guests') - total('waiting'), of: total('guests') })}</strong>
              <span className="muted small">{t('eventsAnswered')}</span></span>
            <span className="ev-stat"><strong>{total('coming')}</strong><span className="muted small">{t('eventsComing')}</span></span>
          </div>
          {upcoming.length > 0
            ? <ul className="ev-list">{upcoming.map(row)}</ul>
            : <p className="muted small">{t('eventsNoneUpcoming')}</p>}
          {past.length > 0 && (
            <details className="ev-past">
              <summary>{t('eventsPast', { n: past.length })}</summary>
              <ul className="ev-list">{past.map(row)}</ul>
            </details>
          )}
        </>
      )}
      {archive.length > 0 && (
        <details className="ev-past ev-archive">
          <summary>{t('eventsArchive', { n: archive.length })}</summary>
          <p className="muted small">{t('eventsArchiveHint')}</p>
          <ul className="ev-list">
            {archive.map((x) => {
              const details = local.get(x.event.id);
              return (
                <li key={x.event.id} className="ev-row archived">
                  <DateLeaf startsOn={dateOf(x, details)} />
                  <span className="ev-main">
                    <strong className="ev-title">{eventTitle(details)}</strong>
                    <span className="muted small">{details ? `${kindLabel(details.kind)} · ${whenOf(details)} · ` : ''}{guestsLabel(x.summary.guests)}</span>
                    <span className="muted small">{t('eventsArchivedOn', {
                      when: ago(x.event.deletedAt ?? x.purgeOn),
                      on: new Date(x.purgeOn).toLocaleDateString(undefined, { month: 'long', day: 'numeric' }),
                    })}</span>
                  </span>
                  <span className="ev-cards">
                    <button className="primary small" onClick={() => void restore(x.event.id)}>{t('eventRestore')}</button>
                    <button className="small danger-text" onClick={() => void forever(x.event.id)}>{t('eventsForever')}</button>
                  </span>
                </li>
              );
            })}
          </ul>
        </details>
      )}
    </section>
  );
}
